import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead, { SITE_URL } from '../../../components/feature/SEOHead';
import { supabase } from '../../../lib/supabase';
import type { Article } from '../../../types/article';
import { timeAgo } from '../../../utils/timeAgo';

const categoryTag: Record<string, string> = {
  Warzone: 'WZ',
  Multiplayer: 'MP',
  Zombies: 'ZM',
  Esports: 'CDL',
  Community: 'COM',
  Leaks: 'LEAK',
};

function Tag({ label }: { label: string }) {
  return (
    <span className="inline-block font-['Barlow_Condensed'] font-bold text-xs tracking-wider text-white bg-[#2a2a2a] border border-[#3a3a3a] px-2 py-0.5">
      {label}
    </span>
  );
}

function RelatedCard({ article }: { article: Article }) {
  const slug = article.slug || article.id;
  return (
    <Link
      to={`/news/${slug}`}
      className="group block border-b border-[#1e1e1e] pb-4 last:border-0 last:pb-0 cursor-pointer"
    >
      <div className="flex gap-3">
        <div className="shrink-0 overflow-hidden" style={{ width: '80px', height: '54px' }}>
          {article.image ? (
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full bg-[#1a1a1a]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-['Barlow_Condensed'] font-black text-white text-xs leading-tight uppercase group-hover:text-[#00ff41] transition-colors line-clamp-2 mb-1">
            {article.title}
          </h4>
          <span className="text-[#555] text-xs font-['Barlow_Condensed'] font-bold">
            {timeAgo(article.date)}
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState<Article[]>([]);
  const [moreNews, setMoreNews] = useState<Article[]>([]);

  useEffect(() => {
    if (!slug) return;
    window.scrollTo(0, 0);

    const fetchData = async () => {
      setLoading(true);

      // Try fetching by slug first, fall back to id for backward compat
      let { data } = await supabase
        .from('articles')
        .select('*')
        .eq('slug', slug)
        .eq('published', true)
        .maybeSingle();

      if (!data) {
        const fallback = await supabase
          .from('articles')
          .select('*')
          .eq('id', slug)
          .eq('published', true)
          .maybeSingle();
        data = fallback.data;
      }

      const fetched = data as Article | null;
      setArticle(fetched);

      if (fetched) {
        const [relRes, moreRes] = await Promise.all([
          supabase
            .from('articles')
            .select('*')
            .eq('published', true)
            .eq('category', fetched.category)
            .neq('id', fetched.id)
            .order('date', { ascending: false })
            .limit(4),
          supabase
            .from('articles')
            .select('*')
            .eq('published', true)
            .neq('id', fetched.id)
            .order('date', { ascending: false })
            .limit(3),
        ]);
        setRelated((relRes.data as Article[]) ?? []);
        setMoreNews((moreRes.data as Article[]) ?? []);
      }

      setLoading(false);
    };

    fetchData();
  }, [slug]);

  /* ── Loading ─────────────────────────────────────── */
  if (loading) {
    return (
      <div className="bg-[#111] min-h-screen">
        <Navbar />
        <div className="pt-14 flex items-center justify-center min-h-[60vh]">
          <i className="ri-loader-4-line animate-spin text-[#00ff41] text-3xl" />
        </div>
      </div>
    );
  }

  /* ── Not found ───────────────────────────────────── */
  if (!article) {
    return (
      <div className="bg-[#111] min-h-screen">
        <SEOHead
          title="Article Not Found | CallOfDutyNext.com"
          description="This article could not be found."
          canonical={`/news/${slug}`}
          noIndex
        />
        <Navbar />
        <div className="pt-14">
          <div className="border-b border-[#1e1e1e] bg-[#0d0d0d]">
            <div className="max-w-7xl mx-auto px-6 py-5">
              <h1 className="font-['Barlow_Condensed'] font-black text-white text-3xl uppercase tracking-wider">
                Article Not Found
              </h1>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center py-32 text-center px-6">
            <i className="ri-article-line text-5xl text-[#333] mb-4" />
            <p className="text-[#555] text-sm mb-6">This article may have been removed or doesn&apos;t exist.</p>
            <Link
              to="/news"
              className="font-['Barlow_Condensed'] font-black text-sm tracking-widest bg-[#00ff41] text-black px-6 py-3 hover:bg-[#00e63a] transition-colors cursor-pointer whitespace-nowrap"
            >
              BACK TO NEWS
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const formattedDate = new Date(article.date).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'News', item: `${SITE_URL}/news` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `${SITE_URL}/news/${article.slug ?? article.id}` },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: article.title,
    description: article.excerpt ?? article.title,
    url: `${SITE_URL}/news/${article.slug ?? article.id}`,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'CallOfDutyNext.com',
      url: SITE_URL,
    },
    image: article.image
      ? { '@type': 'ImageObject', url: article.image }
      : undefined,
    articleSection: article.category,
    keywords: `Call of Duty, ${article.category}, CoD news, Warzone`,
    isPartOf: { '@type': 'WebSite', name: 'CallOfDutyNext.com', url: SITE_URL },
    breadcrumb: breadcrumbSchema,
  };

  /* ── Article ─────────────────────────────────────── */
  return (
    <div className="bg-[#111] min-h-screen">
      <SEOHead
        title={`${article.title} | CallOfDutyNext.com`}
        description={article.excerpt ?? `${article.title} — Read the full story on CallOfDutyNext.com`}
        keywords={`${article.category}, Call of Duty, CoD news, Warzone, ${article.author}`}
        canonical={`/news/${article.slug ?? article.id}`}
        ogType="article"
        ogImage={article.image}
        schema={[articleSchema, breadcrumbSchema]}
        articleMeta={{
          publishedTime: article.date,
          modifiedTime: article.date,
          author: article.author,
          section: article.category,
        }}
      />
      <Navbar />

      <div className="pt-14">
        {/* ── Hero image ─────────────────────────── */}
        {article.image && (
          <div className="relative w-full overflow-hidden" style={{ height: '460px' }}>
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-[#111]/50 to-transparent" />
            {/* Breadcrumb over image */}
            <nav className="absolute bottom-6 left-0 right-0 max-w-7xl mx-auto px-6">
              <div className="flex items-center gap-2 text-xs text-[#888]">
                <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
                <i className="ri-arrow-right-s-line text-[#444]" />
                <Link to="/news" className="hover:text-white transition-colors cursor-pointer">News</Link>
                <i className="ri-arrow-right-s-line text-[#444]" />
                <span className="text-[#666] truncate max-w-xs">{article.title}</span>
              </div>
            </nav>
          </div>
        )}

        {/* ── Title bar (no hero) ─────────────────── */}
        {!article.image && (
          <div className="border-b border-[#1e1e1e] bg-[#0d0d0d]">
            <div className="max-w-7xl mx-auto px-6 py-5">
              <nav className="flex items-center gap-2 text-xs text-[#555] mb-2">
                <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
                <i className="ri-arrow-right-s-line" />
                <Link to="/news" className="hover:text-white transition-colors cursor-pointer">News</Link>
                <i className="ri-arrow-right-s-line" />
                <span className="truncate max-w-xs text-[#444]">{article.title}</span>
              </nav>
              <div className="flex items-center gap-2">
                <Tag label={categoryTag[article.category] ?? article.category.toUpperCase()} />
                <Tag label="NEWS" />
              </div>
            </div>
          </div>
        )}

        {/* ── Main content + sidebar ──────────────── */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 lg:gap-10">

            {/* ── LEFT: Article ──────────────────── */}
            <article className="lg:col-span-2 border-r-0 lg:border-r border-[#1e1e1e] pr-0 lg:pr-10">

              {/* Tags + timestamp */}
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Tag label={categoryTag[article.category] ?? article.category.toUpperCase()} />
                  <Tag label="NEWS" />
                  {article.featured && <Tag label="FEATURED" />}
                </div>
                <span className="text-[#555] text-xs font-['Barlow_Condensed'] font-bold tracking-wider">
                  {timeAgo(article.date)}
                </span>
              </div>

              {/* Title */}
              <h1 className="font-['Barlow_Condensed'] font-black text-white uppercase leading-tight mb-5"
                style={{ fontSize: 'clamp(28px, 4vw, 52px)' }}>
                {article.title}
              </h1>

              {/* Author row */}
              <div className="flex items-center gap-4 py-4 border-y border-[#1e1e1e] mb-6">
                <div className="w-9 h-9 flex items-center justify-center bg-[#1a1a1a] border border-[#2a2a2a] shrink-0">
                  <i className="ri-user-line text-[#555] text-sm" />
                </div>
                <div className="flex-1">
                  <p className="font-['Barlow_Condensed'] font-black text-white text-sm uppercase">
                    {article.author}
                  </p>
                  <p className="text-[#555] text-xs">
                    {formattedDate} &nbsp;·&nbsp; {article.read_time} min read
                  </p>
                </div>
                {/* Share row */}
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[#444] text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mr-1">Share</span>
                  {[
                    { icon: 'ri-twitter-x-line', label: 'X' },
                    { icon: 'ri-facebook-line', label: 'FB' },
                    { icon: 'ri-link', label: 'Copy' },
                  ].map((s) => (
                    <button
                      key={s.label}
                      onClick={() => {
                        if (s.label === 'Copy') navigator.clipboard.writeText(window.location.href);
                      }}
                      aria-label={s.label}
                      className="w-7 h-7 flex items-center justify-center border border-[#2a2a2a] text-[#555] hover:text-white hover:border-[#444] transition-all cursor-pointer"
                    >
                      <i className={`${s.icon} text-xs`} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Excerpt / pull quote */}
              {article.excerpt && (
                <div className="border-l-2 border-[#00ff41] pl-5 mb-8">
                  <p className="text-[#aaa] text-base leading-relaxed italic">{article.excerpt}</p>
                </div>
              )}

              {/* ── Article body ───────────────── */}
              {article.content ? (
                <div
                  className="article-content"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              ) : (
                <div className="border border-dashed border-[#2a2a2a] p-8 text-center text-[#444]">
                  <i className="ri-article-line text-3xl mb-2 block" />
                  <p className="text-sm">Full article content coming soon.</p>
                </div>
              )}

              {/* ── Tags footer ────────────────── */}
              <div className="flex items-center gap-2 flex-wrap mt-10 pt-6 border-t border-[#1e1e1e]">
                <span className="text-[#444] text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mr-1">Tags:</span>
                <Tag label={categoryTag[article.category] ?? article.category.toUpperCase()} />
                <Tag label={article.category.toUpperCase()} />
                <Tag label="NEWS" />
              </div>
            </article>

            {/* ── RIGHT: Sidebar ─────────────── */}
            <aside className="lg:col-span-1 pt-8 lg:pt-0">

              {/* Author card */}
              <div className="border border-[#1e1e1e] bg-[#0d0d0d] mb-6">
                <div className="px-5 py-3 border-b border-[#1e1e1e]">
                  <h3 className="font-['Barlow_Condensed'] font-black text-white text-xs uppercase tracking-widest">
                    Author
                  </h3>
                </div>
                <div className="p-5 flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#1a1a1a] border border-[#2a2a2a] shrink-0">
                    <i className="ri-user-line text-[#555]" />
                  </div>
                  <div>
                    <p className="font-['Barlow_Condensed'] font-black text-white text-base uppercase">
                      {article.author}
                    </p>
                    <p className="text-[#555] text-xs mt-0.5">CallOfDutyNext Writer</p>
                  </div>
                </div>
              </div>

              {/* Article info */}
              <div className="border border-[#1e1e1e] bg-[#0d0d0d] mb-6">
                <div className="px-5 py-3 border-b border-[#1e1e1e]">
                  <h3 className="font-['Barlow_Condensed'] font-black text-white text-xs uppercase tracking-widest">
                    Article Info
                  </h3>
                </div>
                <div className="divide-y divide-[#1e1e1e]">
                  {[
                    { label: 'Published', value: formattedDate },
                    { label: 'Category', value: article.category },
                    { label: 'Read Time', value: `${article.read_time} min` },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center justify-between px-5 py-3">
                      <span className="text-[#555] text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase">
                        {row.label}
                      </span>
                      <span className="text-white text-xs font-['Barlow_Condensed'] font-bold">
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Related in same category */}
              {related.length > 0 && (
                <div className="border border-[#1e1e1e] bg-[#0d0d0d] mb-6">
                  <div className="px-5 py-3 border-b border-[#1e1e1e]">
                    <h3 className="font-['Barlow_Condensed'] font-black text-white text-xs uppercase tracking-widest">
                      More {article.category}
                    </h3>
                  </div>
                  <div className="p-4 space-y-4">
                    {related.map((rel) => (
                      <RelatedCard key={rel.id} article={rel} />
                    ))}
                  </div>
                </div>
              )}

              {/* Latest news */}
              {moreNews.length > 0 && (
                <div className="border border-[#1e1e1e] bg-[#0d0d0d]">
                  <div className="px-5 py-3 border-b border-[#1e1e1e] flex items-center justify-between">
                    <h3 className="font-['Barlow_Condensed'] font-black text-white text-xs uppercase tracking-widest">
                      Latest News
                    </h3>
                    <Link to="/news" className="text-[#555] text-xs font-['Barlow_Condensed'] font-bold hover:text-white transition-colors cursor-pointer">
                      ALL &rarr;
                    </Link>
                  </div>
                  <div className="p-4 space-y-4">
                    {moreNews.map((a) => (
                      <RelatedCard key={a.id} article={a} />
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </div>

      {/* Article body styles */}
      <style>{`
        .article-content {
          color: #aaa;
          font-size: 1rem;
          line-height: 1.8;
        }
        .article-content h2 {
          font-family: 'Barlow Condensed', sans-serif;
          font-weight: 900;
          font-size: 1.6rem;
          color: #fff;
          text-transform: uppercase;
          margin: 2rem 0 0.75rem;
          letter-spacing: 0.02em;
        }
        .article-content h3 {
          font-family: 'Barlow Condensed', sans-serif;
          font-weight: 700;
          font-size: 1.25rem;
          color: #e5e5e5;
          text-transform: uppercase;
          margin: 1.5rem 0 0.5rem;
        }
        .article-content p {
          margin-bottom: 1.25rem;
          color: #999;
        }
        .article-content ul {
          list-style: none;
          padding-left: 0;
          margin: 1rem 0 1.25rem;
        }
        .article-content ul li {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          padding: 0.4rem 0;
          color: #888;
          font-size: 0.9rem;
          border-bottom: 1px solid #1e1e1e;
        }
        .article-content ul li::before {
          content: '';
          display: inline-block;
          width: 6px;
          height: 6px;
          background: #00ff41;
          margin-top: 0.5rem;
          flex-shrink: 0;
        }
        .article-content ol {
          padding-left: 1.5rem;
          margin: 1rem 0 1.25rem;
          counter-reset: item;
        }
        .article-content ol li {
          color: #888;
          font-size: 0.9rem;
          margin-bottom: 0.5rem;
          padding-left: 0.25rem;
        }
        .article-content a {
          color: #00ff41;
          text-decoration: underline;
        }
        .article-content a:hover {
          color: #fff;
        }
        .article-content strong {
          color: #fff;
          font-weight: 700;
        }
        .article-content em {
          color: #bbb;
          font-style: italic;
        }
        .article-content blockquote {
          border-left: 2px solid #00ff41;
          margin: 1.5rem 0;
          padding: 0.75rem 1.25rem;
          background: #0d0d0d;
          color: #aaa;
          font-style: italic;
        }
        .article-content img {
          width: 100%;
          height: auto;
          margin: 1.5rem 0;
        }
        .article-content hr {
          border: none;
          border-top: 1px solid #1e1e1e;
          margin: 2rem 0;
        }
      `}</style>

      <Footer />
    </div>
  );
}
